import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, XCircle, ArrowRight, Home } from 'lucide-react';
import { SEOHead } from '../hooks/usePageSEO';
import { BackNav } from '../components/shared';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 8;          // 8 tentatives
const POLL_INTERVAL_MS = 2500; // toutes les 2.5s

const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking'); // checking | paid | unpaid | error | timeout
  const [details, setDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let cancelled = false;
    let attemptCount = 0;

    const poll = async () => {
      if (cancelled) return;
      attemptCount += 1;
      setAttempts(attemptCount);

      try {
        const res = await fetch(`${BACKEND_URL}/api/payment/checkout-status/${sessionId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setDetails(data);

        if (data.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
        if (data.status === 'expired' || data.status === 'canceled') {
          setStatus('unpaid');
          return;
        }
        if (attemptCount >= MAX_POLLS) {
          setStatus('timeout');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (cancelled) return;
        if (attemptCount >= MAX_POLLS) {
          setStatus('error');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main className="pt-32 min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black" data-testid="payment-success-page">
      <SEOHead title="Paiement | Body-Up" description="Confirmation de votre paiement Body-Up" path="/payment/success" />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <BackNav />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-8 bg-gradient-to-br from-zinc-900 to-black border border-white/10 rounded-3xl p-8 sm:p-12 text-center shadow-2xl"
        >
          {status === 'checking' && (
            <div data-testid="payment-status-checking">
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Vérification du paiement…</h1>
              <p className="text-zinc-400">Tentative {attempts} / {MAX_POLLS} — merci de patienter quelques secondes.</p>
            </div>
          )}

          {status === 'paid' && (
            <div data-testid="payment-status-paid">
              <div className="w-20 h-20 rounded-full bg-green-500/20 border border-green-400/40 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-12 h-12 text-green-400" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Paiement confirmé !</h1>
              <p className="text-zinc-300 mb-6">
                Merci pour votre confiance. Vous recevrez un email de confirmation dans quelques instants.
              </p>
              {details && (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-6 inline-block text-left">
                  <div className="text-zinc-400 text-sm">Montant : <span className="text-white font-bold">{((details.amount_total || 0) / 100).toFixed(2)} {(details.currency || 'CHF').toUpperCase()}</span></div>
                  <div className="text-zinc-400 text-sm">Session : <code className="text-zinc-300 text-xs">{sessionId?.slice(0, 30)}…</code></div>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                <Link to="/" className="px-6 py-3 bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl font-semibold inline-flex items-center justify-center gap-2 hover:scale-105 transition-transform" data-testid="success-home-btn">
                  <Home className="w-5 h-5" /> Retour à l&apos;accueil
                </Link>
                <Link to="/espace-client" className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold inline-flex items-center justify-center gap-2 transition-colors">
                  Mon espace client <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          )}

          {status === 'unpaid' && (
            <div data-testid="payment-status-unpaid">
              <XCircle className="w-16 h-16 text-yellow-400 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-white mb-3">Paiement non finalisé</h1>
              <p className="text-zinc-400 mb-6">La session a expiré ou a été annulée. Vous pouvez réessayer depuis la page Tarifs.</p>
              <Link to="/tarifs" className="px-6 py-3 bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl font-semibold inline-flex items-center gap-2">
                Voir les tarifs <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          )}

          {status === 'timeout' && (
            <div data-testid="payment-status-timeout">
              <Loader2 className="w-16 h-16 text-zinc-500 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-white mb-3">Confirmation en cours</h1>
              <p className="text-zinc-400 mb-4">Votre paiement est en cours de traitement. Vous recevrez un email de confirmation dès que tout est validé.</p>
              <p className="text-zinc-500 text-sm mb-6">Pas d&apos;inquiétude — aucun débit n&apos;est effectué tant que le paiement n&apos;est pas confirmé par Stripe.</p>
              <Link to="/" className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold inline-flex items-center gap-2">
                <Home className="w-5 h-5" /> Retour à l&apos;accueil
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="payment-status-error">
              <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-white mb-3">Erreur de vérification</h1>
              <p className="text-zinc-400 mb-6">Impossible de récupérer le statut du paiement. Contactez-nous si le problème persiste.</p>
              <a href="mailto:info@body-up.ch" className="px-6 py-3 bg-gradient-to-r from-primary to-pink-500 text-white rounded-xl font-semibold inline-flex items-center gap-2">
                Nous contacter
              </a>
            </div>
          )}
        </motion.div>
      </div>
    </main>
  );
};

export default PaymentSuccessPage;
